// src/pages/UserManagement.js
import React, { useState, useEffect } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import '../App.css'; // Import custom CSS for sticky header
import "./UserManagement.css";
import api from "../utils/apiHelper";
import { APP_API_URL } from "../constants/appURLConstrants";
import { hasRole } from "../utils/authHelper";
import { isStrongPassword, passwordPolicyMessage } from "../utils/passwordPolicy";

// ======================
// ROLE PERMISSION REFERENCE
// (static reference of what each role can do across the system)
// ======================
const ROLE_PERMISSIONS = [
  {
    role: "ADMIN",
    moduleAccess: "All modules",
    addData: true,
    editData: true,
    deleteDisable: true,
    approveData: true,
    userManagement: "Full (create / edit / disable / reset password)",
    dataScope: "All provinces & districts",
  },
  {
    role: "NPCO",
    moduleAccess: "All modules",
    addData: true,
    editData: false,
    deleteDisable: false,
    approveData: false,
    userManagement: "View only (no create / edit / disable)",
    dataScope: "All provinces & districts",
  },
  {
    role: "WFP",
    moduleAccess: "CB for Villagers, 1A1, 1A2, 1A3a, 1A3b, 1A5a, 1A5b, 3Act1a, 3Act1b",
    addData: true,
    editData: false,
    deleteDisable: false,
    approveData: false,
    userManagement: "No access",
    dataScope: "All provinces & districts",
  },
  {
    role: "PROVINCE",
    moduleAccess: "All modules except User Management",
    addData: true,
    editData: false,
    deleteDisable: false,
    approveData: false,
    userManagement: "No access",
    dataScope: "Own province only",
  },
  {
    role: "DISTRICT",
    moduleAccess: "All modules except User Management",
    addData: true,
    editData: false,
    deleteDisable: false,
    approveData: false,
    userManagement: "No access",
    dataScope: "Own district only",
  },
];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [sortOrder, setSortOrder] = useState("DESC");

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [showRolePermModal, setShowRolePermModal] = useState(false);

  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);



  const [form, setForm] = useState({
    name: "",
    surname: "",
    username: "",
    phone_number: "",
    password: "",
    role: "DISTRICT",
    province_id: "",
    district_id: ""
  });


  //check password complexity and show hint message
  const passwordError =
    form.password && !isStrongPassword(form.password)
      ? passwordPolicyMessage()
      : "";



  // ======================
  // FETCH USERS
  // ======================
  const fetchUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api.get(`${APP_API_URL}/api/users`, {
        params: { search, role, sortOrder }
      });

      if (res.data.success) {
        setUsers(res.data.data);
      } else {
        setUsers([]);
        setError("Failed to load users");
      }
    } catch (err) {
      setError("Server error while loading users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [search, role, sortOrder]);

  // ======================
  // INPUT CHANGE
  // ======================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ======================
  // OPEN CREATE MODAL
  // ======================
  const openCreateModal = () => {
    setForm({
      name: "",
      surname: "",
      username: "",
      phone_number: "",
      password: "",
      role: "DISTRICT",
      province_id: "",
      district_id: ""
    });

    setEditMode(false);
    setSelectedUserId(null);
    setShowModal(true);
  };

  // ======================
  // OPEN EDIT MODAL
  // ======================
  const openEditModal = (user) => {
    setForm({
      name: user.name || "",
      surname: user.surname || "",
      username: user.username || "",
      phone_number: user.phone_number || "",
      password: "",
      role: user.role || "DISTRICT",
      province_id: user.province_id || "",
      district_id: user.district_id || ""
    });

    setSelectedUserId(user.id);
    setEditMode(true);
    setShowModal(true);
  };

  // ======================
  // CREATE / UPDATE
  // ======================
  const handleSubmit = async (e) => {
    e.preventDefault();

    const passwordValue = form.password;

    // ✅ enforce password policy on CREATE and when password is being changed in EDIT
    if (!editMode) {
      if (!isStrongPassword(passwordValue)) {
        alert(passwordPolicyMessage());
        return;
      }
    }

    if (editMode && passwordValue) {
      if (!isStrongPassword(passwordValue)) {
        alert(passwordPolicyMessage());
        return;
      }
    }


    try {
      const sessionUser = JSON.parse(sessionStorage.getItem("user"));

      if (editMode) {
        await api.put(`${APP_API_URL}/api/users/${selectedUserId}`, {
          ...form,
          updated_by: sessionUser.id
        });
      } else {
        await api.post(`${APP_API_URL}/api/users/register`, {
          ...form,
          created_by: sessionUser.id
        });
      }

      setShowModal(false);
      setEditMode(false);
      setSelectedUserId(null);
      fetchUsers();

    } catch (err) {
      console.error(err);
      alert("Server error");
    }
  };

  // ======================
  // TOGGLE ACTIVE
  // ======================
  const toggleUser = async (id) => {
    try {
      const sessionUser = JSON.parse(sessionStorage.getItem("user"));
      await api.patch(`${APP_API_URL}/api/users/${id}/toggle-active`, {
        updated_by: sessionUser.id
      });
      fetchUsers();
    } catch (err) {
      console.error(err);
    }
  };

  // ======================
  // RESET PASSWORD
  // ======================
  const resetPassword = async (id) => {
    try {
      const sessionUser = JSON.parse(sessionStorage.getItem("user"));
      await api.patch(`${APP_API_URL}/api/users/${id}/reset-password`, {
        updated_by: sessionUser.id
      });

      // Update the user's must_change_password status in the UI immediately after reset
      setUsers((prev) =>
        prev.map((u) =>
          u.id === id
            ? { ...u, must_change_password: true }
            : u
        )
      );
      alert("Password reset to default: user@AFN12345");
    } catch (err) {
      console.error(err);
    }
  };

  // ======================
  // PHONE FORMAT
  // ======================
  const formatPhone = (phone) => {
    if (!phone) return "-";
    return phone.replace(/(\d{3})(\d{4})(\d+)/, "$1-$2-$3");
  };



  //Fetch provinces and districts for dropdowns in the form
  const fetchProvinces = async () => {
    try {
      const res = await api.get(`${APP_API_URL}/api/users/provinces`);
      if (res.data.success) setProvinces(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };
  //Fetch districts for dropdowns in the form
  const fetchDistricts = async (province_id) => {
    if (!province_id) {
      setDistricts([]);
      return;
    }

    try {
      const res = await api.get(
        `${APP_API_URL}/api/users/districts/${province_id}`
      );

      if (res.data.success) setDistricts(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  //Load provinces ONCE when component mounts
  useEffect(() => {
    fetchProvinces();
  }, []);



  const isDisabled = (field) => {
    const r = form.role;
    if (r === "ADMIN" || r === "NPCO" || r === "WFP") return true;
    if (r === "PROVINCE" && field === "district") return true;
    return false;
  };

  const handleRoleChange = (e) => {
    const value = e.target.value;

    setForm((prev) => ({
      ...prev,
      role: value,
      province_id: "",
      district_id: ""
    }));

    setDistricts([]);
  };

  const handleProvinceChange = (e) => {
    const value = e.target.value;

    setForm((prev) => ({
      ...prev,
      province_id: value,
      district_id: ""
    }));

    fetchDistricts(value);
  };






  if (loading) {
    return <div className="user-mm-page">Loading users...</div>;
  }

  return (
    <div className="user-mm-page">

      {/* HEADER */}
      <div className="user-mm-header">
        <h2>User Management</h2>

        <div className="user-mm-header-actions">
          {hasRole("ADMIN") && (
            <button className="user-mm-btn-primary" onClick={openCreateModal}>
              + Create User
            </button>
          )}

          {hasRole("ADMIN") && (
            <button
              className="user-mm-btn-primary"
              onClick={() => setShowRolePermModal(true)}
            >
              Show Role Permission
            </button>
          )}
        </div>
      </div>

      {/* ERROR */}
      {error && <div className="user-mm-error">{error}</div>}

      {/* FILTERS */}
      <div className="user-mm-filters">
        <input
          placeholder="Search username / name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All Roles</option>
          <option value="ADMIN">ADMIN</option>
          <option value="NPCO">NPCO</option>
          <option value="WFP">WFP</option>
          <option value="PROVINCE">PROVINCE</option>
          <option value="DISTRICT">DISTRICT</option>
        </select>

        <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
          <option value="DESC">Newest</option>
          <option value="ASC">Oldest</option>
        </select>
      </div>



      {/* Main Table */}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div>
          <div className="table-responsive" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
            <table className="table table-hover table-sm text-nowrap">
              <thead className="table-success sticky-header">
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Province</th>
                  <th>District</th>
                  <th style={{ textAlign: "center" }}>User Status</th>
                  <th style={{ textAlign: "center" }}>Password Status</th>
                  <th style={{ textAlign: "center" }}>Last Logon</th>
                  <th className="user-mm-center" style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="user-mm-user-info">
                        <div className="user-mm-avatar">
                          {u.name?.charAt(0)}{u.surname?.charAt(0)}
                        </div>
                        <div className="user-mm-name">
                          {u.name} {u.surname}
                        </div>
                      </div>
                    </td>

                    <td className="user-mm-username">{u.username}</td>
                    <td>{formatPhone(u.phone_number)}</td>

                    <td>
                      <span className={`user-mm-role ${u.role.toLowerCase()}`}>
                        {u.role}
                      </span>
                    </td>

                    {/* <td>{u.province_id || "-"}</td>
                              <td>{u.district_id || "-"}</td> */}
                    <td style={{ fontFamily: "'Phetsarath OT', sans-serif" }}>{u.province_name_la || "-"}</td>
                    <td style={{ fontFamily: "'Phetsarath OT', sans-serif" }}>{u.district_name_la || "-"}</td>

                    <td style={{ textAlign: "center" }}>
                      <span className={`user-mm-status ${u.is_active ? "active" : "inactive"}`}>
                        {u.is_active ? "Active" : "Disabled"}
                      </span>
                    </td>

                    <td style={{ textAlign: "center" }}>
                      <span
                        className={`user-mm-status ${u.must_change_password ? "warning" : "active"
                          }`}
                      >
                        {u.must_change_password ? "To change at logon" : "OK"}
                      </span>
                    </td>

                    <td style={{ textAlign: "center" }}>
                      {u.last_logon_time
                        ? new Date(u.last_logon_time).toLocaleString("en-GB", {
                            day: "2-digit", month: "2-digit", year: "numeric",
                            hour: "2-digit", minute: "2-digit"
                          })
                        : "-"}
                    </td>

                    <td className="user-mm-center">
                      {hasRole("ADMIN") && (
                        <>
                          <button
                            className="user-mm-btn-small user-mm-btn-edit"
                            onClick={() => openEditModal(u)}
                          >
                            Edit
                          </button>

                          <button
                            className="user-mm-btn-small user-mm-btn-danger"
                            onClick={() => toggleUser(u.id)}
                          >
                            {u.is_active ? "Disable" : "Enable"}
                          </button>

                          <button
                            className="user-mm-btn-small"
                            onClick={() => resetPassword(u.id)}
                          >
                            Reset
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
      )}


      {/* MODAL */}
      {showModal && (
        <div className="user-mm-modal-overlay">
          <div className="user-mm-modal">

            <h3>{editMode ? "Edit User" : "Create User"}</h3>

            <form onSubmit={handleSubmit}>

              <input name="name" value={form.name} onChange={handleChange} placeholder="Name" required />
              <input name="surname" value={form.surname} onChange={handleChange} placeholder="Surname" required />
              <input name="username" value={form.username} onChange={handleChange} placeholder="Username" required />
              <input name="phone_number" value={form.phone_number} onChange={handleChange} placeholder="Phone" />

              <input
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                placeholder={editMode ? "New Password (optional)" : "Password"}
                required={!editMode}
              />

              {/* Password complexity hint */}
              {passwordError && (
                <div className="password-hint" style={{ color: "red", fontSize: "12px", marginBottom: "10px" }}>
                  {passwordError}
                </div>
              )}

              <select name="role" value={form.role} onChange={handleChange}>
                <option value="ADMIN">ADMIN</option>
                <option value="NPCO">NPCO</option>
                <option value="WFP">WFP</option>
                <option value="PROVINCE">PROVINCE</option>
                <option value="DISTRICT">DISTRICT</option>
              </select>

              {/* Province Dropdown */}
              {/* <select name="province_id" value={form.province_id} onChange={(e) => {
                const value = e.target.value;
                setForm((prev) => ({ ...prev, province_id: value, district_id: "" }));
                fetchDistricts(value);
              }}
              >
                <option value="">Select Province</option>
                {provinces.map((p) => (<option key={p.province_id} value={p.province_id}>{p.province_name_la}</option>))}
              </select> */}

              {/* District Dropdown */}
              {/* <select name="district_id" value={form.district_id} onChange={handleChange} disabled={!form.province_id}>
                <option value="">Select District</option>{districts.map((d) => (<option key={d.district_id} value={d.district_id}>{d.district_name_la}</option>))}
              </select> */}

              {/* Province Dropdown */}
              <select
                name="province_id"
                value={form.province_id}
                onChange={handleProvinceChange}
                disabled={isDisabled("province")}
              >
                <option value="">Select Province</option>
                {provinces.map((p) => (
                  <option key={p.province_id} value={p.province_id}>
                    {p.province_name_la}
                  </option>
                ))}
              </select>

              {/* District Dropdown */}
              <select
                name="district_id"
                value={form.district_id}
                onChange={handleChange}
                disabled={isDisabled("district") || !form.province_id}
              >
                <option value="">Select District</option>
                {districts.map((d) => (
                  <option key={d.district_id} value={d.district_id}>
                    {d.district_name_la}
                  </option>
                ))}
              </select>



              <div className="user-mm-modal-actions">

                <button className="user-mm-btn-primary" type="submit">
                  {editMode ? "Update" : "Create"}
                </button>

                <button
                  type="button"
                  className="user-mm-btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>

              </div>

            </form>

          </div>
        </div>
      )}

      {/* ROLE PERMISSION MODAL */}
      {showRolePermModal && (
        <div className="user-mm-modal-overlay">
          <div className="user-mm-modal user-mm-modal-wide">

            <h3>Role Permissions</h3>

            <div className="table-responsive">
              <table className="table table-bordered table-sm user-mm-perm-table">
                <thead className="table-success">
                  <tr>
                    <th>Role</th>
                    <th>Module Access</th>
                    <th style={{ textAlign: "center" }}>Add Data</th>
                    <th style={{ textAlign: "center" }}>Edit Data</th>
                    <th style={{ textAlign: "center" }}>Delete / Disable</th>
                    <th style={{ textAlign: "center" }}>Approve Data</th>
                    <th>User Management</th>
                    <th>Data Scope</th>
                  </tr>
                </thead>
                <tbody>
                  {ROLE_PERMISSIONS.map((p) => (
                    <tr key={p.role}>
                      <td>
                        <span className={`user-mm-role ${p.role.toLowerCase()}`}>
                          {p.role}
                        </span>
                      </td>
                      <td>{p.moduleAccess}</td>
                      <td style={{ textAlign: "center" }}>
                        {p.addData ? "✅" : "❌"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {p.editData ? "✅" : "❌"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {p.deleteDisable ? "✅" : "❌"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {p.approveData ? "✅" : "❌"}
                      </td>
                      <td>{p.userManagement}</td>
                      <td>{p.dataScope}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="user-mm-modal-actions">
              <button
                type="button"
                className="user-mm-btn-secondary"
                onClick={() => setShowRolePermModal(false)}
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}
    </div>

    //End of return
  );
}